import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { FirmSettings } from 'src/app/core/entity/firm.settings';

@Component({
  selector: 'app-firm-assignment-settings',
  templateUrl: './firm-assignment-settings.component.html',
  styleUrls: ['./firm-assignment-settings.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirmAssignmentSettingsComponent implements OnDestroy {
  assignmentSettingsForm: UntypedFormGroup;
  resetDialog: boolean;
  maxLength = 500;

  @Input() set firmAssignmentData$(firmAssignmentData$: any) {
    const { ...firmAssignmentData } = firmAssignmentData$;
    this.assignmentSettingsForm.patchValue(this.booleanifyFormValues(firmAssignmentData));
  }

  @Output() updateFirmAssignmentData = new EventEmitter();
  @Output() resetFirmAssignmentData = new EventEmitter();

  private onDestroyBS: Subject<boolean>;

  constructor(private formBuilder: UntypedFormBuilder) {
    this.onDestroyBS = new Subject();
    this.setAssignmentSettingsForm();
  }

  ngOnDestroy() {
    this.onDestroyBS.next(true);
  }

  // eslint-disable-next-line max-lines-per-function
  private setAssignmentSettingsForm(): void {
    this.assignmentSettingsForm = this.formBuilder.group({
      other_condition_text: [''],
      other_terms_text: [''],
      srf_text: [''],
      srf_or_far: [''],
      far_text: [''],
      use_far: [false],
      use_srf: [false],
      notice_period: [''],
      payment_condition_text1: [''],
      payment_condition_text2: [''],
      payment_condition_text3: [''],
      payment_condition_text4: [''],
      payment_text1: [''],
      payment_text2: [''],
      payment_text3: [''],
      payment_text4: [''],
      attachment_text2: [''],
      attachment_text1: [''],
      attachment_text3: [''],
      attachment_text4: [''],
      material_delivery1: [''],
      material_delivery2: [''],
      material_delivery3: [''],
      material_delivery4: [''],
      show_payment_condition_text1: [false],
      show_payment_condition_text2: [false],
      show_payment_condition_text3: [false],
      show_payment_condition_text4: [false],
      show_payment_text1: [false],
      show_payment_text2: [false],
      show_payment_text3: [false],
      show_payment_text4: [false],
      show_attachment_text1: [false],
      show_attachment_text2: [false],
      show_attachment_text3: [false],
      show_attachment_text4: [false],
    });
  }

  updateAssignmentSettingsData() {
    this.updateFirmAssignmentData.emit(this.stringifyFormValues(this.assignmentSettingsForm.value));
  }

  resetAssignmentSettings() {
    this.resetFirmAssignmentData.emit();
    this.resetDialog = false;
  }

  onSrfOrFarToggle(checked: boolean, id: string) {
    const useFar = this.assignmentSettingsForm.get('use_far');
    const useSrf = this.assignmentSettingsForm.get('use_srf');
    if (id === 'far') {
      useFar.setValue(checked);
      useSrf.setValue(false);
    } else if (id === 'srf') {
      useSrf.setValue(checked);
      useFar.setValue(false);
    }

    this.assignmentSettingsForm.get('srf_or_far').setValue(checked ? id : '');
  }

  private booleanifyFormValues(formValue: FirmSettings): FirmSettings {
    const booleanifiedFormValue = { ...formValue };
    Object.keys(booleanifiedFormValue).forEach((key) => {
      if (booleanifiedFormValue[key] === 'true' || booleanifiedFormValue[key] === 'false') {
        booleanifiedFormValue[key] = this.stringToBoolean(booleanifiedFormValue[key] as string);
      }
    });
    return booleanifiedFormValue;
  }

  private stringifyFormValues(formValue: FirmSettings): FirmSettings {
    const stringifiedFormValue = { ...formValue };
    Object.keys(stringifiedFormValue).forEach((key) => {
      if (typeof stringifiedFormValue[key] === 'boolean') {
        stringifiedFormValue[key] = this.booleanToString(stringifiedFormValue[key] as boolean);
      }
    });
    return stringifiedFormValue;
  }

  private booleanToString(value: boolean): string {
    return value ? 'true' : 'false';
  }

  private stringToBoolean(value: string): boolean {
    return value === 'true';
  }
}
